/**
 * Lists tab on the SMS page — subscriber lists for broadcasts, drip
 * campaigns, keyword join/leave, and folder notifications. Clicking a row
 * opens the list detail page (members + import + drip editor).
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { smsLists, type SmsListRow } from '@/api/smsLists';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { useScopedNavigate } from '@/hooks/useScopedNavigate';
import { formatRef } from '@/lib/formatRef';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  ListChecks, Plus, Search, Users, Timer, ChevronRight,
} from 'lucide-react';

export function ListsTab({ canMutate }: { canMutate: boolean }) {
  const customerId = useEffectiveCustomerId();
  const navigate = useScopedNavigate();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['sms-lists', customerId, search],
    queryFn: () => smsLists.list({
      customer_id: customerId ?? undefined,
      search: search.trim() || undefined,
    }),
  });
  const rows = data?.rows ?? [];

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-4 flex items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
            <Input
              placeholder="Search lists…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          {canMutate && (
            <Button size="sm" className="ms-auto" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 me-1.5" />
              New list
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Loading lists…</div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center">
            <ListChecks className="mx-auto h-7 w-7 text-slate-300 mb-3" />
            <p className="text-sm font-medium text-slate-500">
              {search ? 'No lists match.' : 'No subscriber lists yet'}
            </p>
            <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
              Lists power bulk broadcasts, drip campaigns, JOIN keywords, and
              new-recording alerts on folders.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border -mx-2">
            {rows.map((l) => (
              <ListRowItem
                key={l.id}
                row={l}
                onOpen={() => navigate(`/sms/lists/${l.id}`)}
              />
            ))}
          </ul>
        )}
      </CardContent>
      {canMutate && <CreateListDialog open={createOpen} onOpenChange={setCreateOpen} />}
    </Card>
  );
}

function ListRowItem({ row, onOpen }: { row: SmsListRow; onOpen: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="w-full flex items-center gap-3 px-2 py-3 text-left hover:bg-secondary/40 transition-colors rounded-md"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium truncate">{row.name}</span>
            <span className="font-mono text-[10px] text-muted-foreground shrink-0">
              {formatRef(row.ref_number, 'SML')}
            </span>
          </div>
          {row.description && (
            <p className="mt-0.5 text-xs text-muted-foreground truncate">{row.description}</p>
          )}
        </div>
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground tabular-nums shrink-0">
          <Users className="h-3.5 w-3.5" />
          {row.member_count ?? 0}
        </span>
        {(row.drip_step_count ?? 0) > 0 && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-sky-700 ring-1 ring-sky-200 shrink-0"
            title={`${row.drip_step_count} drip step${row.drip_step_count === 1 ? '' : 's'} active`}
          >
            <Timer className="h-3 w-3" />
            Drip
          </span>
        )}
        <ChevronRight className="h-4 w-4 text-slate-300 shrink-0 rtl:rotate-180" />
      </button>
    </li>
  );
}

function CreateListDialog({
  open, onOpenChange,
}: {
  open: boolean; onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const navigate = useScopedNavigate();
  // Backend requires customer_id for super_admin / reseller_owner — always
  // forward when we have one (CLAUDE.md "forwarding customer_id" pattern).
  const customerId = useEffectiveCustomerId();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => smsLists.create({
      name: name.trim(),
      description: description.trim() || null,
      ...(customerId ? { customer_id: customerId } : {}),
    }),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['sms-lists'] });
      setName(''); setDescription(''); setError(null);
      onOpenChange(false);
      navigate(`/sms/lists/${row.id}`);
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : 'Create failed.'),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New SMS list</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="sms-list-name">Name</Label>
            <Input
              id="sms-list-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Daily shiur subscribers"
              maxLength={200}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="sms-list-desc">Description (optional)</Label>
            <Input
              id="sms-list-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Who is on this list and why"
              maxLength={1000}
            />
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={create.isPending}>Cancel</Button>
          </DialogClose>
          <Button
            type="button"
            disabled={create.isPending || !name.trim()}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Creating…' : 'Create list'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
